import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Users, Building2, Briefcase, FileText, 
  Lock, Unlock, Check, X, ChevronDown, ChevronUp,
  Loader2, AlertCircle, Save, Mail, LogOut, User
} from 'lucide-react';
import './PlatformAdmin.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Feature catalogue - add new features here
const FEATURE_CATALOGUE = [
  { key: 'custom_deal_scoring', label: 'Custom Deal Scoring', description: 'AI-powered deal quality scoring' },
  { key: 'advanced_analytics', label: 'Advanced Analytics', description: 'Enhanced reporting and dashboards' },
  { key: 'bulk_operations', label: 'Bulk Operations', description: 'Bulk import/export and actions' },
  { key: 'custom_integrations', label: 'Custom Integrations', description: 'Third-party API integrations' },
  { key: 'white_label', label: 'White Label', description: 'Custom branding and domain' },
  { key: 'priority_support', label: 'Priority Support', description: 'Dedicated support channel' },
];

export default function PlatformAdmin() {
  const [email, setEmail] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [orgDeals, setOrgDeals] = useState([]);
  const [activeTab, setActiveTab] = useState('features');
  
  // Check for saved token in sessionStorage
  useEffect(() => {
    const savedToken = sessionStorage.getItem('platform_admin_token');
    const savedEmail = sessionStorage.getItem('platform_admin_email');
    if (savedToken) {
      setToken(savedToken);
      setAdminEmail(savedEmail || '');
      validateAndLoad(savedToken, savedEmail);
    }
  }, []);
  
  const validateAndLoad = async (authToken, email) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Validate token by fetching stats
      const statsRes = await axios.get(`${API_URL}/api/platform/stats`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      setStats(statsRes.data);
      setAdminEmail(statsRes.data.adminEmail || email || '');
      setIsAuthenticated(true);
      
      // Load orgs
      const orgsRes = await axios.get(`${API_URL}/api/platform/orgs`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setOrgs(orgsRes.data);
    } catch (err) {
      // Token expired or invalid
      sessionStorage.removeItem('platform_admin_token');
      sessionStorage.removeItem('platform_admin_email');
      setError('Session expired. Please log in again.');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  const login = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${API_URL}/api/platform/login`, {
        email,
        secret
      });
      
      const { token: newToken, email: returnedEmail } = res.data;
      
      sessionStorage.setItem('platform_admin_token', newToken);
      sessionStorage.setItem('platform_admin_email', returnedEmail);
      
      setToken(newToken);
      setAdminEmail(returnedEmail);
      setIsAuthenticated(true);
      
      // Clear secret from state for security
      setSecret('');
      
      // Load dashboard
      validateAndLoad(newToken, returnedEmail);
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed';
      setError(message);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadDashboard = async () => {
    try {
      // Load stats
      const statsRes = await axios.get(`${API_URL}/api/platform/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);
      
      // Load orgs
      const orgsRes = await axios.get(`${API_URL}/api/platform/orgs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgs(orgsRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  };
  
  const selectOrg = async (org) => {
    setSelectedOrg(org);
    setIsLoading(true);
    
    try {
      // Load org details
      const detailsRes = await axios.get(`${API_URL}/api/platform/orgs/${org.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgDetails(detailsRes.data);
      
      // Load members
      const membersRes = await axios.get(`${API_URL}/api/platform/orgs/${org.id}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgMembers(membersRes.data);
      
      // Load deals
      const dealsRes = await axios.get(`${API_URL}/api/platform/orgs/${org.id}/deals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgDeals(dealsRes.data);
      
      setActiveTab('features');
    } catch (err) {
      console.error('Failed to load org details:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleFeature = async (featureKey, enabled) => {
    if (!selectedOrg) return;
    
    try {
      await axios.post(
        `${API_URL}/api/platform/orgs/${selectedOrg.id}/features/${featureKey}`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setOrgDetails(prev => ({
        ...prev,
        features: {
          ...prev.features,
          [featureKey]: enabled
        }
      }));
    } catch (err) {
      console.error('Failed to toggle feature:', err);
      alert('Failed to update feature');
    }
  };
  
  const handleLogout = () => {
    sessionStorage.removeItem('platform_admin_token');
    sessionStorage.removeItem('platform_admin_email');
    setIsAuthenticated(false);
    setToken('');
    setAdminEmail('');
    setEmail('');
    setSecret('');
    setStats(null);
    setOrgs([]);
    setSelectedOrg(null);
  };
  
  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="platform-admin-login">
        <div className="login-card">
          <div className="login-icon">
            <Shield size={48} />
          </div>
          <h1>DealNexa Platform Admin</h1>
          <p className="login-subtitle">Super-admin access only</p>
          
          {error && (
            <div className="login-error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
          
          <div className="login-field">
            <Mail size={18} className="field-icon" />
            <input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && login()}
              className="login-input"
            />
          </div>
          
          <div className="login-field">
            <Lock size={18} className="field-icon" />
            <input
              type="password"
              placeholder="Password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && login()}
              className="login-input"
            />
          </div>
          
          <button 
            onClick={login}
            disabled={isLoading || !email || !secret}
            className="login-button"
          >
            {isLoading ? <Loader2 className="spin" size={20} /> : <Shield size={20} />}
            {isLoading ? 'Authenticating...' : 'Access Platform'}
          </button>
        </div>
      </div>
    );
  }
  
  // Dashboard
  return (
    <div className="platform-admin">
      {/* Header */}
      <header className="platform-header">
        <div className="platform-header-left">
          <Shield className="platform-logo" size={28} />
          <h1>DealNexa Platform Admin</h1>
        </div>
        <div className="platform-header-right">
          <div className="admin-user">
            <User size={16} />
            <span>{adminEmail}</span>
          </div>
          <span className="platform-badge">Super Admin</span>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </header>
      
      <div className="platform-content">
        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <Building2 size={24} className="stat-icon" />
              <div className="stat-value">{stats.organizations}</div>
              <div className="stat-label">Organizations</div>
            </div>
            <div className="stat-card">
              <Users size={24} className="stat-icon" />
              <div className="stat-value">{stats.users}</div>
              <div className="stat-label">Users</div>
            </div>
            <div className="stat-card">
              <Briefcase size={24} className="stat-icon" />
              <div className="stat-value">{stats.deals}</div>
              <div className="stat-label">Deals</div>
            </div>
            <div className="stat-card">
              <FileText size={24} className="stat-icon" />
              <div className="stat-value">{stats.documents}</div>
              <div className="stat-label">Documents</div>
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <div className="platform-main">
          {/* Org List Sidebar */}
          <div className="org-list">
            <h3>Organizations ({orgs.length})</h3>
            <div className="org-items">
              {orgs.map(org => (
                <div
                  key={org.id}
                  className={`org-item ${selectedOrg?.id === org.id ? 'active' : ''}`}
                  onClick={() => selectOrg(org)}
                >
                  <div className="org-item-header">
                    <span className="org-name">{org.name}</span>
                    <span className="org-type">{org.type}</span>
                  </div>
                  <div className="org-meta">
                    <span>{org.member_count} members</span>
                    <span>{org.deal_count} deals</span>
                    {org.feature_count > 0 && (
                      <span className="org-features-badge">
                        {org.feature_count} features
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Org Details Panel */}
          <div className="org-details">
            {selectedOrg ? (
              <>
                <div className="org-details-header">
                  <h2>{selectedOrg.name}</h2>
                  <span className="org-id">ID: {selectedOrg.id}</span>
                </div>
                
                {/* Tabs */}
                <div className="org-tabs">
                  <button
                    className={activeTab === 'features' ? 'active' : ''}
                    onClick={() => setActiveTab('features')}
                  >
                    <Unlock size={16} />
                    Features
                  </button>
                  <button
                    className={activeTab === 'members' ? 'active' : ''}
                    onClick={() => setActiveTab('members')}
                  >
                    <Users size={16} />
                    Members ({orgMembers.length})
                  </button>
                  <button
                    className={activeTab === 'deals' ? 'active' : ''}
                    onClick={() => setActiveTab('deals')}
                  >
                    <Briefcase size={16} />
                    Deals ({orgDeals.length})
                  </button>
                </div>
                
                {/* Tab Content */}
                <div className="tab-content">
                  {activeTab === 'features' && (
                    <div className="features-tab">
                      <h4>Feature Flags</h4>
                      <p className="tab-description">
                        Toggle features for this organization. Changes are saved immediately.
                      </p>
                      <div className="features-list">
                        {FEATURE_CATALOGUE.map(feature => {
                          const isEnabled = orgDetails?.features?.[feature.key] || false;
                          return (
                            <div key={feature.key} className="feature-item">
                              <div className="feature-info">
                                <span className="feature-name">{feature.label}</span>
                                <span className="feature-desc">{feature.description}</span>
                              </div>
                              <button
                                className={`feature-toggle ${isEnabled ? 'enabled' : ''}`}
                                onClick={() => toggleFeature(feature.key, !isEnabled)}
                              >
                                {isEnabled ? (
                                  <><Check size={16} /> Enabled</>
                                ) : (
                                  <><X size={16} /> Disabled</>
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'members' && (
                    <div className="members-tab">
                      <h4>Organization Members</h4>
                      <div className="members-list">
                        {orgMembers.map(member => (
                          <div key={member.id} className="member-item">
                            <div className="member-avatar">
                              {member.full_name?.[0]?.toUpperCase() || member.email[0].toUpperCase()}
                            </div>
                            <div className="member-info">
                              <span className="member-name">{member.full_name || 'No name'}</span>
                              <span className="member-email">{member.email}</span>
                            </div>
                            <span className={`member-role ${member.role}`}>
                              {member.role}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'deals' && (
                    <div className="deals-tab">
                      <h4>Organization Deals</h4>
                      <div className="deals-list">
                        {orgDeals.map(deal => (
                          <div key={deal.id} className="deal-item">
                            <div className="deal-info">
                              <span className="deal-name">{deal.name}</span>
                              <span className="deal-stage">{deal.stage}</span>
                            </div>
                            <div className="deal-meta">
                              <span>Owner: {deal.owner?.full_name || 'Unknown'}</span>
                              <span>Created: {new Date(deal.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-org-selected">
                <Building2 size={48} />
                <p>Select an organization to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
